import { useMemo, useCallback, useRef, useEffect } from 'react';
import { Text, Flex, Box, IconButton, Button } from '@radix-ui/themes';
import {
  Cross2Icon,
  Pencil1Icon,
  ChevronLeftIcon,
} from '@radix-ui/react-icons';
import type { SnippetDetail, SnippetVersion } from '@/v2/entities/KnowledgeSnippet';
import { SnippetStatus } from '@/v2/entities/KnowledgeSnippet/KnowledgeSnippet.model';
import { getActiveVersion } from '@/v2/entities/KnowledgeSnippet';
import { CheckmarkBadgeIcon } from '@/v2/assets/icons/CheckmarkBadgeIcon';
import styles from './VersionHistorySidePanel.module.scss';

interface VersionHistorySidePanelProps {
  snippetDetail: SnippetDetail;
  selectedVersionId: string | null;
  onVersionSelect: (versionId: string | null) => void;
  onClose: () => void;
}

export function VersionHistorySidePanel({
  snippetDetail,
  selectedVersionId,
  onVersionSelect,
  onClose,
}: VersionHistorySidePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  const activeVersion = useMemo(() => {
    return getActiveVersion(snippetDetail);
  }, [snippetDetail]);

  // Handle outside click to close panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add event listener after a short delay to avoid immediate close on open
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const { currentVersions, verifiedVersions, draftVersions } = useMemo(() => {
    const current: SnippetVersion[] = [];
    const verified: SnippetVersion[] = [];
    const drafts: SnippetVersion[] = [];

    snippetDetail.versions.forEach((version) => {
      if (version.status === SnippetStatus.VERIFIED) {
        current.push(version);
      } else if (version.status === SnippetStatus.OUTDATED) {
        verified.push(version);
      } else {
        drafts.push(version);
      }
    });

    return {
      currentVersions: current,
      verifiedVersions: verified,
      draftVersions: drafts,
    };
  }, [snippetDetail.versions]);

  const handleVersionClick = useCallback(
    (versionId: string) => {
      onVersionSelect(versionId);
    },
    [onVersionSelect]
  );

  const handleBackToCurrent = useCallback(() => {
    if (activeVersion) {
      onVersionSelect(activeVersion.id);
    }
    onClose();
  }, [activeVersion, onVersionSelect, onClose]);

  const isCurrentVersion = useCallback(
    (versionId: string) => {
      return versionId === activeVersion?.id;
    },
    [activeVersion]
  );

  return (
    <Box ref={panelRef} className={styles.sidePanel} width="400px">
      <Flex className={styles.container} direction="column" height="100%">
        <Flex className={styles.header} align="center" gap="2" p="3">
          <IconButton
            variant="ghost"
            size="2"
            onClick={onClose}
            aria-label="Close"
          >
            <Cross2Icon width={16} height={16} />
          </IconButton>
          <Text size="3" weight="medium" className={styles.headerTitle}>Version History</Text>
        </Flex>

        <Flex
          direction="column"
          gap="4"
          p="4"
          style={{ flex: 1, overflowY: 'auto' }}
        >
          <Button
            variant="soft"
            size="2"
            className={styles.backButton}
            onClick={handleBackToCurrent}
          >
            <ChevronLeftIcon width={14} height={14} />
            <Text size="2">Back to Current Version</Text>
          </Button>

          {draftVersions.length > 0 && (
            <Flex className={styles.versionSection} direction="column" gap="2">
              <Text size="2" weight="medium" className={styles.sectionLabel}>
                Draft versions
              </Text>
              <Flex direction="column" gap="1">
                {draftVersions.map((version) => {
                  const isSelected =
                    selectedVersionId === version.id || (!selectedVersionId && isCurrentVersion(version.id));

                  return (
                    <Flex
                      key={version.id}
                      className={`${styles.versionItem} ${isSelected ? styles.selected : ''}`}
                      onClick={() => handleVersionClick(version.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleVersionClick(version.id);
                        }
                      }}
                      align="center"
                      justify="between"
                      px="2"
                    >
                      <Flex align="center" gap="2" style={{ flex: 1, minWidth: 0 }}>
                        <Pencil1Icon width={16} height={16} className={styles.draftIcon} />
                        <Text size="2" weight="medium" className={styles.versionTitle}>
                          {version.versionName || 'Draft version'}
                        </Text>
                      </Flex>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
          )}

          {currentVersions.length > 0 && (
            <Flex className={styles.versionSection} direction="column" gap="2">
              <Text size="2" weight="medium" className={styles.sectionLabel}>
                Current
              </Text>
              <Flex direction="column" gap="1">
                {currentVersions.map((version) => {
                  const isSelected =
                    selectedVersionId === version.id || (!selectedVersionId && isCurrentVersion(version.id));

                  return (
                    <Flex
                      key={version.id}
                      className={`${styles.versionItem} ${isSelected ? styles.selected : ''}`}
                      onClick={() => handleVersionClick(version.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleVersionClick(version.id);
                        }
                      }}
                      align="center"
                      justify="between"
                      px="2"
                    >
                      <Flex align="center" gap="2" style={{ flex: 1, minWidth: 0 }}>
                        <CheckmarkBadgeIcon size={16} fill="green" />
                        <Text size="2" weight="medium" className={styles.versionTitle}>
                          {version.versionName || 'Current version'}
                        </Text>
                      </Flex>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
          )}

          {verifiedVersions.length > 0 && (
            <Flex className={styles.versionSection} direction="column" gap="2">
              <Text size="2" weight="medium" className={styles.sectionLabel}>
                Verified
              </Text>
              <Flex direction="column" gap="1">
                {verifiedVersions.map((version) => {
                  const isSelected =
                    selectedVersionId === version.id || (!selectedVersionId && isCurrentVersion(version.id));

                  return (
                    <Flex
                      key={version.id}
                      className={`${styles.versionItem} ${isSelected ? styles.selected : ''}`}
                      onClick={() => handleVersionClick(version.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleVersionClick(version.id);
                        }
                      }}
                      align="center"
                      justify="between"
                      px="2"
                    >
                      <Flex align="center" gap="2" style={{ flex: 1, minWidth: 0 }}>
                        <CheckmarkBadgeIcon size={16} fill="green" />
                        <Text size="2" weight="medium" className={styles.versionTitle}>
                          {version.versionName || 'Verified version'}
                        </Text>
                      </Flex>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}
